import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { FormikTextarea } from '../../../capacity/containers/IMODescription/IMODescriptionDrawer';
import Drawer from '../../../common/components/Drawer/Drawer';
import { useSnackbar } from 'notistack';
import { Field, Form, Formik } from 'formik';
import {
  McButton,
  McInputDate,
  McSegmentedControl,
  McSegmentedControlItem,
  McSwitch,
} from '@maersk-global/mds-react-wrapper';
import z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import FormikInputField from '../../../common/components/FormikInputField/FormikInputField';
import FormattedMessage from '../../../common/FormattedMessage';
import { eventsService } from '../../../common/service/events/events-service';
import { Event } from '../../../common/service/events/types';
import TimeGroup, { TimeSlot } from '../time-group';
import { mapValuesToDTO } from './helpers';

type EventsDrawerProps = {
  open: boolean;
  onSuccess?: Function;
  onClose: () => void;
  selectedTerminal: string;
  defaultValues?: Event;
  action: 'add' | 'edit';
};

const schema = z
  .object({
    fromDate: z.string(),
    toDate: z.string(),
    name: z.string(),
    gate: z.string(),
    vesselOps: z.string(),
    remarks: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.fromDate > data.toDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please check the dates',
        path: ['fromDate'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please check the dates',
        path: ['toDate'],
      });
    }
  });

const EventsDrawer: FunctionComponent<EventsDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  selectedTerminal,
  defaultValues,
  action,
}) => {
  const [gateTimeSlots, setGateTimesSlots] = useState<TimeSlot[]>([]);
  const [vesselOpsTimeSlots, setVesselOpsTimesSlots] = useState<TimeSlot[]>([]);
  const [gateSlotsError, setGateSlotsError] = useState(false);
  const [vesselOpsSlotsError, setVesselOpsSlotsError] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const service = useMemo(() => eventsService(), []);

  const getFormInitialValues = () => {
    return {
      id: defaultValues?.id,
      name: defaultValues?.name || '',
      terminalCode: defaultValues?.terminalCode || selectedTerminal,
      fromDate: defaultValues?.fromDateTime ? defaultValues.fromDateTime.substring(0, 10) : '',
      toDate: defaultValues?.toDateTime ? defaultValues.toDateTime.substring(0, 10) : '',
      gate: defaultValues?.gate || 'NO',
      vesselOps: defaultValues?.vesselOps || 'NO',
      remarks: defaultValues?.remarks || '',
      gateTimeSlots: defaultValues?.gateTimeSlots || [],
      vesselOpsTimeSlots: defaultValues?.vesselOpsTimeSlots || [],
    };
  };

  useEffect(() => {
    setGateTimesSlots(defaultValues?.gateTimeSlots || []);
    setVesselOpsTimesSlots(defaultValues?.vesselOpsTimeSlots || []);
  }, [defaultValues]);

  const onSubmit = async (values, { setSubmitting }) => {
    if (gateSlotsError || vesselOpsSlotsError) return;
    setSubmitting(true);
    const shouldAppendGateTimeSlots = values.gate === 'PARTIAL';
    const shouldAppendVesselOpsTimeSlots = values.vesselOps === 'PARTIAL';
    const dto = {
      ...mapValuesToDTO(values),
      ...(shouldAppendGateTimeSlots ? { gateTimeSlots: gateTimeSlots } : {}),
      ...(shouldAppendVesselOpsTimeSlots ? { vesselOpsTimeSlots: vesselOpsTimeSlots } : {}),
    };
    try {
      if (action === 'edit') {
        await service.updateEvent(dto);
        enqueueSnackbar('Event updated successfully', { variant: 'success' });
      } else {
        await service.createEvent(dto);
        enqueueSnackbar('Event added successfully', { variant: 'success' });
      }
      onSuccess?.();
      onClose();
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={action === 'add' ? 'Add new event' : 'Edit event'}
      open={open}
      setOpen={onClose}
      onRequestClose={onClose}
      noFooter
      wider
    >
      <Formik
        initialValues={getFormInitialValues()}
        enableReinitialize
        validateOnChange={false}
        validationSchema={toFormikValidationSchema(schema)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => (
          <Form className="HolidaysDrawer__form">
            <div className="HolidaysDrawer__content">
              <div className="EventsDrawer__date-time">
                <div className="EventsDrawer__date-time__input-wrapper">
                  <McInputDate
                    invalid={Boolean(errors.fromDate)}
                    errormessage={errors.fromDate as string}
                    min={`${new Date()}`}
                    max={`${new Date().getFullYear() + 1}-12-31`}
                    label="From"
                    value={values.fromDate}
                    // @ts-ignore
                    input={(e) => setFieldValue('fromDate', e.target.value)}
                  />
                </div>
                <div className="EventsDrawer__date-time__input-wrapper">
                  <McInputDate
                    invalid={Boolean(errors.toDate)}
                    errormessage={errors.toDate as string}
                    min={`${new Date()}`}
                    max={`${new Date().getFullYear() + 1}-12-31`}
                    label="To"
                    value={values.toDate}
                    // @ts-ignore
                    input={(e) => setFieldValue('toDate', e.target.value)}
                  />
                </div>
              </div>
              <br />
              <Field
                label="Name"
                name="name"
                component={FormikInputField}
                errormessage={errors.name}
                invalid={Boolean(errors.name)}
              />
              <br />
              <div className="HolidaysDrawer__periods">
                <McSwitch
                  disabled={!(values.fromDate && values.toDate)}
                  name="gate"
                  style={{ display: 'inline-flex', marginRight: 32 }}
                  fit="small"
                  label="Gate affected"
                  checked={values.gate === 'YES' || values.gate === 'PARTIAL'}
                  change={() =>
                    setFieldValue('gate', values.gate === 'YES' || values.gate === 'PARTIAL' ? 'NO' : 'YES')
                  }
                />
                <McSegmentedControl id="gate" fit="small">
                  <McSegmentedControlItem
                    disabled={values.gate === 'NO'}
                    selected={values.gate === 'YES'}
                    onClick={() => setFieldValue('gate', 'YES')}
                  >
                    <span className="HolidaysDrawer__segemented-option--small">All day</span>
                  </McSegmentedControlItem>
                  <McSegmentedControlItem
                    disabled={values.gate === 'NO'}
                    selected={values.gate === 'PARTIAL'}
                    onClick={() => setFieldValue('gate', 'PARTIAL')}
                  >
                    <span className="HolidaysDrawer__segemented-option--small">Selected period</span>
                  </McSegmentedControlItem>
                </McSegmentedControl>

                {(values.gate === 'YES' || values.gate === 'PARTIAL') && (
                  <TimeGroup
                    type="event"
                    isAllDay={values.gate === 'YES'}
                    slots={values.gateTimeSlots || []}
                    setSlots={setGateTimesSlots}
                    setError={setGateSlotsError}
                    date={values.fromDate}
                    dateTo={values.toDate}
                  />
                )}
              </div>

              <div className="HolidaysDrawer__periods">
                <McSwitch
                  disabled={!(values.fromDate && values.toDate)}
                  name="vesselOps"
                  style={{ display: 'inline-flex', marginRight: 32 }}
                  fit="small"
                  label="Vessel Ops affected"
                  checked={values.vesselOps === 'YES' || values.vesselOps === 'PARTIAL'}
                  change={() =>
                    setFieldValue(
                      'vesselOps',
                      values.vesselOps === 'YES' || values.vesselOps === 'PARTIAL' ? 'NO' : 'YES',
                    )
                  }
                />
                <McSegmentedControl fit="small">
                  <McSegmentedControlItem
                    disabled={values.vesselOps === 'NO'}
                    selected={values.vesselOps === 'YES'}
                    onClick={() => setFieldValue('vesselOps', 'YES')}
                  >
                    <span className="HolidaysDrawer__segemented-option--small">All day</span>
                  </McSegmentedControlItem>
                  <McSegmentedControlItem
                    disabled={values.vesselOps === 'NO'}
                    selected={values.vesselOps === 'PARTIAL'}
                    onClick={() => setFieldValue('vesselOps', 'PARTIAL')}
                  >
                    <span className="HolidaysDrawer__segemented-option--small">Selected period</span>
                  </McSegmentedControlItem>
                </McSegmentedControl>

                {(values.vesselOps === 'YES' || values.vesselOps === 'PARTIAL') && (
                  <TimeGroup
                    type="event"
                    isAllDay={values.vesselOps === 'YES'}
                    slots={values.vesselOpsTimeSlots || []}
                    setSlots={setVesselOpsTimesSlots}
                    setError={setVesselOpsSlotsError}
                    date={values.fromDate}
                    dateTo={values.toDate}
                  />
                )}
              </div>
              <br />
              <Field
                label="Remarks"
                type="text"
                name="remarks"
                rows={2}
                maxlength={100}
                invalid={Boolean(errors.remarks)}
                errormessage={errors.remarks as string}
                component={FormikTextarea}
              />
            </div>
            <div className="HolidaysDrawer__buttons">
              <McButton type="button" appearance="neutral" click={onClose}>
                <FormattedMessage id="cancel" />
              </McButton>
              <McButton disabled={isSubmitting} type="submit" appearance="primary">
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </McButton>
            </div>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default EventsDrawer;
